import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, Heart } from 'lucide-react';
import { useCurrency } from '../../context/CurrencyContext';

const API = process.env.REACT_APP_BACKEND_URL;

export const useAccessStatus = () => {
  const [access, setAccess] = useState(null);
  useEffect(() => {
    const token = localStorage.getItem('customer_token');
    if (!token) return;
    fetch(`${API}/api/engagement/access`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => d && setAccess(d))
      .catch(() => {});
  }, []);
  return access;
};

export const TrialBanner = ({ access }) => {
  const navigate = useNavigate();
  const { formatPrice } = useCurrency();
  if (!access || access.status !== 'trial') return null;
  return (
    <div data-testid="trial-banner" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.75rem', flexWrap: 'wrap', padding: '0.7rem 1rem', borderRadius: 12, marginBottom: '1rem', background: '#EEF2EC', border: '1px solid #8B9D8355' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.9rem', color: '#1B2A41' }}>
        <Heart size={16} style={{ color: '#8B9D83' }} />
        <span>You're on the <strong>free plan</strong> — 1 course, forever. Upgrade for Voice AI or to run more courses at once, from {formatPrice(5)}/month.</span>
      </div>
      <button data-testid="trial-banner-upgrade" onClick={() => navigate('/courses')} style={{ width: 'auto', padding: '0.4rem 0.9rem', borderRadius: 999, border: 'none', background: '#C25E4B', color: '#fff', fontSize: '0.8125rem', fontWeight: 600, cursor: 'pointer' }}>See plans</button>
    </div>
  );
};

// Full-screen "trial ended" gate. Wrap any member page: <TrialGate>…</TrialGate>
export const TrialGate = ({ children, allowPurchasedCoursesLink = true }) => {
  const access = useAccessStatus();
  const navigate = useNavigate();
  const { formatPrice, formatPrice99 } = useCurrency();
  const [hardshipMsg, setHardshipMsg] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [showHardship, setShowHardship] = useState(false);

  const submitHardship = async () => {
    setSending(true);
    try {
      const token = localStorage.getItem('customer_token');
      await fetch(`${API}/api/engagement/hardship-request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ message: hardshipMsg }),
      });
      setSent(true);
    } catch (e) {
      setSent(true);
    } finally {
      setSending(false);
    }
  };

  if (!access || access.has_access) return children;
  return (
    <div data-testid="trial-ended-screen" style={{ minHeight: '100vh', background: '#F2EFE9', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2rem 1.25rem' }}>
      <div style={{ maxWidth: 560, width: '100%', background: '#fff', borderRadius: 20, padding: 'clamp(1.75rem, 5vw, 3rem)', textAlign: 'center', boxShadow: '0 20px 60px rgba(27,42,65,0.12)' }}>
        <div style={{ width: 64, height: 64, borderRadius: '50%', background: '#EEF2EC', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1.25rem' }}><Lock size={28} style={{ color: '#8B9D83' }} /></div>
        <h1 style={{ fontFamily: 'var(--font-heading)', fontSize: 'clamp(1.6rem, 4vw, 2.1rem)', color: '#1B2A41', margin: '0 0 0.75rem', lineHeight: 1.2 }}>Choose a plan to keep going</h1>
        <p style={{ color: '#3d4a5c', lineHeight: 1.7, margin: '0 0 1.5rem' }}>Nothing has been lost — your progress, your support team and your prayers are exactly where you left them. Choose a plan to pick up where you left off — {formatPrice(5)} a month for two courses at once, or {formatPrice99(9.99)} for every course plus Voice AI, for your whole household.</p>
        <div style={{ display: 'grid', gap: '0.5rem', textAlign: 'left', margin: '0 auto 1.75rem', maxWidth: 380, fontSize: '0.95rem', color: '#3d4a5c' }}>
          {['Every week of the course, released in full', 'Daily devotionals to read each morning', 'Accountability: daily pledge, weekly reflection, sobriety count', 'Confidential prayer requests to Pastor Simon and the team', 'Lifetime access — one payment, no subscription'].map((t) => (
            <div key={t} style={{ display: 'flex', gap: '0.6rem', alignItems: 'flex-start' }}><Heart size={16} style={{ color: '#C25E4B', flexShrink: 0, marginTop: 3 }} />{t}</div>
          ))}
        </div>
        <button data-testid="trial-ended-upgrade" onClick={() => navigate('/courses')} style={{ width: 'auto', padding: '0.9rem 1.8rem', borderRadius: 999, border: 'none', background: '#C25E4B', color: '#fff', fontSize: '1rem', fontWeight: 700, cursor: 'pointer' }}>See plans — from {formatPrice(5)}/month</button>
        <p style={{ fontSize: '0.8125rem', color: '#6b6f66', margin: '1.25rem 0 0', lineHeight: 1.6 }}>
          Every level unlocks the same access; the higher ones simply give more.
          {allowPurchasedCoursesLink && <> Bought a course outright? <a href="/member/courses" data-testid="trial-ended-courses-link" style={{ color: '#8B9D83', fontWeight: 600, padding: 0, display: 'inline' }}>It's still yours</a>.</>}
        </p>

        {/* Hardship request */}
        <div style={{ marginTop: '1.5rem', paddingTop: '1.25rem', borderTop: '1px solid #EAE6DD' }}>
          {sent ? (
            <p data-testid="hardship-confirmation" style={{ fontSize: '0.9rem', color: '#1B2A41', lineHeight: 1.6, margin: 0 }}>
              <Heart size={15} style={{ color: '#8B9D83', verticalAlign: '-2px', marginRight: 6 }} />
              Thank you for telling us. We've received your note and will be in touch soon — your access stays open in the meantime.
            </p>
          ) : !showHardship ? (
            <button
              data-testid="hardship-open-btn"
              onClick={() => setShowHardship(true)}
              style={{ width: 'auto', background: 'none', border: 'none', color: '#8B9D83', fontWeight: 600, fontSize: '0.875rem', cursor: 'pointer', textDecoration: 'underline', padding: 0 }}
            >
              I can't afford this right now
            </button>
          ) : (
            <div style={{ textAlign: 'left' }}>
              <label htmlFor="hardship-msg" style={{ display: 'block', fontSize: '0.85rem', color: '#3d4a5c', margin: '0 0 0.5rem', lineHeight: 1.5 }}>
                Money should never be what stops you. Tell us in a line and we'll open your access — no questions, no awkwardness.
              </label>
              <textarea
                id="hardship-msg"
                data-testid="hardship-message-input"
                value={hardshipMsg}
                onChange={(e) => setHardshipMsg(e.target.value)}
                rows={2}
                placeholder="e.g. Things are really tight this month — could I keep going?"
                style={{ width: '100%', borderRadius: 10, border: '1px solid #cdd3c8', padding: '0.6rem 0.75rem', fontSize: '0.9rem', fontFamily: 'inherit', resize: 'vertical', color: '#1B2A41' }}
              />
              <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.6rem' }}>
                <button
                  data-testid="hardship-submit-btn"
                  onClick={submitHardship}
                  disabled={sending || !hardshipMsg.trim()}
                  style={{ width: 'auto', padding: '0.55rem 1.1rem', borderRadius: 999, border: 'none', background: '#8B9D83', color: '#fff', fontSize: '0.85rem', fontWeight: 700, cursor: (sending || !hardshipMsg.trim()) ? 'default' : 'pointer', opacity: (sending || !hardshipMsg.trim()) ? 0.6 : 1 }}
                >
                  {sending ? 'Sending…' : 'Send request'}
                </button>
                <button
                  data-testid="hardship-cancel-btn"
                  onClick={() => setShowHardship(false)}
                  style={{ width: 'auto', padding: '0.55rem 1rem', borderRadius: 999, border: '1px solid #cdd3c8', background: '#fff', color: '#6b6f66', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer' }}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
